import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, ClipboardList } from "lucide-react";
import { Callout, EmptyState, PanelButton, StatusBadge, cn } from "../../ui";
import Shimmer from "../../Common/Shimmer";
import apiClient from "../../../utils/apiClient";
import { formatDue, isDueSoon, relativeTime } from "../../Assignments/general/assignmentRules";

/**
 * AssignmentsPanel — the batch's assignments on My Learning, grouped by what
 * the student has to do: To do · Handed in · Graded · Missed or closed.
 *
 * Data: GET /general-assignments/student/list?batchId= → { assignments }, each
 * with the server's `submissionState` — the same answer the assignment page
 * reads, so a row never says "To do" for something the page won't take.
 * Open: /s/batch-assignment/:id.
 */

const GROUPS = [
  { key: "todo", title: "To do", states: ["resubmit_requested", "not_submitted"] },
  { key: "submitted", title: "Handed in", states: ["submitted"] },
  { key: "graded", title: "Graded", states: ["graded"] },
  { key: "past", title: "Missed or closed", states: ["missed", "closed"] },
];

/** From the server; a response from before `submissionState` existed falls back to the submission. */
const stateOf = (a) => {
  if (a.submissionState?.state) return a.submissionState.state;
  if (a.submission) return a.submission.status;
  if (a.status === "closed") return "closed";
  return a.dueDate && new Date(a.dueDate) < new Date() ? "missed" : "not_submitted";
};

const deadlineOf = (a) => a.submissionState?.deadline || a.dueDate;

const whenLine = (a, state) => {
  const deadline = deadlineOf(a);
  const dateOnly = a.submissionState?.dateOnly;
  switch (state) {
    case "resubmit_requested":
      return "Changes requested — resubmit when ready";
    case "submitted":
      return a.submission?.submittedAt ? `Handed in ${formatDue(a.submission.submittedAt)}` : "Handed in";
    case "graded":
      return a.submission?.gradedAt ? `Graded ${formatDue(a.submission.gradedAt)}` : "Graded";
    case "missed":
      return deadline ? `Was due ${formatDue(deadline, { dateOnly })}` : "Missed";
    case "closed":
      return "No longer taking submissions";
    default:
      if (!deadline) return "No due date";
      return a.submissionState?.pastDue
        ? `Late — was due ${formatDue(deadline, { dateOnly })}`
        : `Due ${relativeTime(deadline)} · ${formatDue(deadline, { dateOnly })}`;
  }
};

const byDeadline = (dir) => (a, b) => dir * (new Date(deadlineOf(a) || 0) - new Date(deadlineOf(b) || 0));

const AssignmentRow = ({ a, state, onOpen }) => {
  const soon = state === "not_submitted" && isDueSoon(deadlineOf(a));
  const graded = state === "graded" && a.submission?.marks != null;
  return (
    <li>
      <PanelButton onClick={onOpen}>
        <span
          className={cn(
            "tw-flex tw-h-9 tw-w-9 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-sm",
            soon ? "tw-bg-warning/10 tw-text-warning" : "tw-bg-primary/10 tw-text-primary",
          )}
        >
          <ClipboardList size={18} aria-hidden="true" />
        </span>
        <span className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col">
          <span className="tw-truncate tw-text-sm tw-font-medium tw-text-content">{a.title}</span>
          <span className={cn("tw-truncate tw-text-xs", soon ? "tw-text-warning-fg" : "tw-text-content-subtle")}>
            {whenLine(a, state)}
          </span>
        </span>
        {graded && (
          <span className="tw-shrink-0 tw-text-sm tw-tabular-nums">
            <span className="tw-font-semibold tw-text-content">{a.submission.marks}</span>
            <span className="tw-text-content-subtle"> / {a.maxMarks}</span>
          </span>
        )}
        <StatusBadge status={state} domain="assignmentWork" className="tw-shrink-0 max-[575.98px]:tw-hidden" />
        <ChevronRight size={16} className="tw-shrink-0 tw-text-content-subtle" aria-hidden="true" />
      </PanelButton>
    </li>
  );
};

const AssignmentsPanel = ({ batchId }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [assignments, setAssignments] = useState([]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setFailed(false);
    apiClient
      .get("/general-assignments/student/list", { params: batchId ? { batchId } : undefined })
      .then(({ data }) => alive && setAssignments(data?.assignments || []))
      .catch(() => {
        if (!alive) return;
        setAssignments([]);
        setFailed(true);
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [batchId]);

  const groups = useMemo(() => GROUPS.map((g) => {
    const items = assignments.filter((a) => g.states.includes(stateOf(a)));
    // What is due soonest first; everything already past, most recent first.
    items.sort(g.key === "todo" ? byDeadline(1) : byDeadline(-1));
    return { ...g, items };
  }).filter((g) => g.items.length), [assignments]);

  if (loading) {
    return (
      <div className="tw-flex tw-flex-col tw-gap-2" aria-busy="true" aria-label="Loading assignments">
        <Shimmer height={60} count={3} />
      </div>
    );
  }
  if (failed) return <Callout tone="danger" role="alert">Your assignments could not be loaded. Reload the page to try again.</Callout>;
  if (!assignments.length) {
    return (
      <EmptyState
        icon={ClipboardList}
        title="No assignments yet"
        description="Assignments shared with your batch will appear here."
      />
    );
  }

  return (
    <div className="tw-flex tw-flex-col tw-gap-5">
      {groups.map((g) => (
        <section key={g.key} aria-labelledby={`ml-assignments-${g.key}`} className="tw-flex tw-flex-col tw-gap-2">
          <h3 id={`ml-assignments-${g.key}`} className="tw-m-0 tw-text-[0.8125rem] tw-font-semibold tw-text-content-muted">
            {g.title} <span className="tw-font-normal tw-text-content-subtle">· {g.items.length}</span>
          </h3>
          <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-2 tw-p-0">
            {g.items.map((a) => (
              <AssignmentRow key={a._id} a={a} state={stateOf(a)} onOpen={() => navigate(`/s/batch-assignment/${a._id}`)} />
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
};

export default AssignmentsPanel;
